import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { screenMd } from 'constants/styles/breakpoints';
import { PopularReportsProvider, ReportsContext } from 'contexts/reporting';
import { SessionContext } from 'contexts/session';
import {
  CUSTOM_REPORTS_RESTRICTED_COPY,
  ENTERPRISE_PLAN_URL,
  REPORTING_TEST_IDS,
  RESTRICTED_CUSTOM_REPORTS_BANNER_COLORS,
  USER_ACTIVITY,
} from 'constants/reporting';
import Button from 'components/button';
import ReportingContentPopular from './components/ReportingContentPopular';
import ReportingContentFilter from './components/ReportingContentFilter';
import ReportingFilters from './components/ReportingFilters';
import ReportingHistoryTable from './components/ReportingHistory/ReportingHistoryTable';
import ReportsTopBar from './components/ReportsTopBar';
import PredefinedReportsTab from './components/PredefinedReportsTab';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ScheduledReportsTable from './components/ReportingScheduled/ScheduledReportsTable';
import { isMobile } from 'react-device-detect';
import Notification from 'components/notification';
import { downloadReportGroupAction } from 'contexts/reporting/actions';
import Icon from 'components/icons';
import { useCapabilities } from 'contexts/capabilities';
import { ReactComponent as RestrictedReportsLock } from 'assets/icons/lock.svg';

const Reporting = {
  Container: styled.div<{ fetching: boolean }>`
    position: relative;
    padding-bottom: 0;
  `,
};

const MainContent = styled.div`
  background: var(--white);
  border-radius: 10px;
  padding: 10px;

  ${screenMd} {
    padding: 30px;
  }
`;

const PopularTitle = styled.h5`
  margin: 0 0 8px;
  color: var(--black-dark, #1a2535);
  font-family: var(--font-family-base);
  font-size: 20px;
  font-weight: 900;
  line-height: 26px; /* 130% */
  padding: 20px;
`;

const CollapsedBannerContainer = styled.div<{ $collapsed: boolean }>``;

const CollapsedBanner = styled.button<{ $embedded?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border: none;
  cursor: pointer;
  padding: 10px;
  border-radius: 10px;
  background: ${({ theme }) => theme.default.colors.palette.Business_LightThemeGreyLighter};
  color: ${({ theme }) => theme.default.colors.fonts.default};
  margin: 10px 0 10px;
  text-align: left;
  transition: background 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;

  /* Default */
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);

  .banner-text {
    margin: 0 auto;
    font-size: 12px;
    line-height: 14px;
  }
  .banner-text strong {
    color: ${({ theme }) => theme.default.colors.palette.Business_DataVizLilacLighter};
    font-weight: 700;
    margin-right: 4px;
  }

  ${screenMd} {
    margin: 20px 0 20px;
    padding: 14px 16px;

    .banner-text {
      font-size: 16px;
      line-height: 24px;
    }
  }

  .chevron-pill {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 991px) {
    gap: 12px;

    .banner-text {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      margin: 0 12px;
    }

    .banner-text strong {
      display: block;
      margin-right: 0;
    }
  }

  /* Hover */
  &:hover {
    background: #ffffff;
    box-shadow: inset 0 0 0 1px rgba(167, 147, 246, 0.25);
  }
  &:hover .chevron-pill {
    background: ${({ theme }) => theme.default.colors.palette.Business_LightThemeGreyLighter};
  }

  /* Pressed */
  &:active {
    background: rgba(167, 147, 246, 0.1);
    box-shadow: inset 0 0 0 1px rgba(167, 147, 246, 0.35);
    filter: saturate(1.05);
  }
  &:active .chevron-pill {
    background: #ffffff;
    box-shadow: 0 0 0 1px rgba(167, 147, 246, 0.35);
  }
  ${({ $embedded }) =>
    $embedded &&
    css`
      border-radius: 10px 10px 0 0;
      border: 1px solid var(--grey-lighter);
      border-bottom: none;
      margin-bottom: 0 !important;
      padding: 10px;
      background: transparent;
      box-shadow: none;
      &:hover {
        background: transparent;
        box-shadow: none;
      }
      &:active {
        background: transparent;
        box-shadow: none;
        filter: none;
      }
    `}
`;

const CustomSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
  overflow-y: visible;

  background: var(--white);
  border-radius: 0 0 10px 10px;
  border: 1px solid var(--grey-lighter);
  border-top: none;

  ${screenMd} {
    gap: 20px;
    overflow: visible;
    margin-bottom: 20px;
  }
`;

const CustomSectionCollapsable = styled(CustomSection)<{ $open: boolean; $maxHeight: number }>`
  /* Keep ancestor overflow visible so sticky descendants work */
  overflow: visible;
  opacity: ${(p) => (p.$open ? 1 : 0)};
  transition: opacity 300ms ease;

  /* Clip only the non-sticky content */
  .collapsible-clip {
    border-radius: 10px;
    overflow-y: hidden;
    overflow-x: visible;
    max-height: ${(p) => (p.$open ? `${p.$maxHeight}px` : '0')};
    transition: max-height 400ms ease;
    will-change: max-height;
  }
`;

const tabs = {
  predefined: 'Predefined reports',
  custom: 'Custom reports',
  scheduled: 'Manage my scheduled reports',
};

const MobileFloatingButtonWrapper = styled.div`
  position: fixed;
  left: 50%;
  bottom: calc(env(safe-area-inset-bottom, 0px) + 80px);
  transform: translateX(-50%);
  width: calc(100% - 32px);
  max-width: 360px;
  display: flex;
  justify-content: center;
  z-index: 210;

  button {
    width: 100%;
    box-shadow: 0 12px 24px rgba(26, 37, 53, 0.18);
  }

  ${screenMd} {
    display: none;
  }
`;

const SectionDivider = styled.div`
  color: var(--grey-lighter);
  height: 1px;
  align-self: stretch;
  background: var(--grey-lighter);
  ${screenMd} {
    margin: 30px 0;
  }
`;

const RestrictedCustomReportsBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 20px 0;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.default.colors.palette.Business_LightThemeGreyLighter};
  border-radius: 10px;
  background: ${RESTRICTED_CUSTOM_REPORTS_BANNER_COLORS.background};
  text-align: center;

  .lock-icon {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    color: ${RESTRICTED_CUSTOM_REPORTS_BANNER_COLORS.accent};

    svg {
      display: block;
      width: 24px;
      height: 24px;
    }
  }

  .copy {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px;
    align-items: baseline;
    color: ${({ theme }) => theme.default.colors.fonts.darkGray};
    font-family: var(--font-family-base);
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
  }

  strong {
    color: ${({ theme }) => theme.default.colors.fonts.dark};
    font-weight: 700;
  }

  a {
    color: ${RESTRICTED_CUSTOM_REPORTS_BANNER_COLORS.accent};
    font-weight: 400;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:focus-visible {
      outline: 2px solid ${RESTRICTED_CUSTOM_REPORTS_BANNER_COLORS.accent};
      outline-offset: 3px;
      border-radius: 2px;
    }
  }

  ${screenMd} {
    padding: 16px 20px;
  }

  @media (max-width: 575px) {
    flex-direction: column;
    gap: 8px;

    .copy {
      width: 100%;
    }
  }
`;

const ReportingContent = (): JSX.Element => {
  const { fetching, saveReportsFiltersPreferences } = useContext(ReportsContext);
  const { accounts } = useContext(SessionContext);
  const [activeFilterTab, setActiveFilterTab] = useState(USER_ACTIVITY);
  const [activeTab, setActiveTab] = useState(tabs.custom);
  const { useAutomatedReports } = useFlags();
  const location = useLocation();
  const history = useHistory();
  const [customSectionCollapsed, setCustomSectionCollapsed] = useState(true);
  const { getUsers } = useContext(ReportsContext);
  const { canCreateCustomReports } = useCapabilities();

  // Collapsible measurement state
  const collapsibleInnerRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [maxHeight, setMaxHeight] = useState(0);
  const handledDownloadIdRef = useRef<string | null>(null);

  const handleDownload = useCallback(
    async (id: string) => {
      try {
        const reportUrlResponse = await downloadReportGroupAction(id);
        const downloadReport = () => {
          window.open(reportUrlResponse.result.download_url);
        };

        downloadReport();

        Notification({
          type: 'info',
          duration: null,
          text: (
            <p className="m-0">
              Download didn&apos;t start?{' '}
              <a className="text-decoration-underline text-white" onClick={downloadReport}>
                Click here to get your report.
              </a>
            </p>
          ),
        });
      } catch (error) {
        Notification({
          text: 'There was a problem generating the download link for your report.',
          type: 'error',
        });
      } finally {
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('downloadId');
        history.replace({ search: searchParams.toString() });
      }
    },
    [history, location.search]
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const downloadId = searchParams.get('downloadId');

    if (!downloadId) {
      handledDownloadIdRef.current = null;
      return;
    }

    if (canCreateCustomReports) {
      setActiveTab(tabs.scheduled);
    }

    if (handledDownloadIdRef.current === downloadId) return;

    handledDownloadIdRef.current = downloadId;
    handleDownload(downloadId);
  }, [canCreateCustomReports, handleDownload, location.search]);

  useEffect(() => {
    if (canCreateCustomReports) {
      getUsers('');
    }
  }, [canCreateCustomReports, getUsers]);

  // Measure content height and observe changes; re-run on tab change
  useEffect(() => {
    if (!canCreateCustomReports) return;
    const el = collapsibleInnerRef.current;
    if (!el) return;

    const measure = () => {
      setContentHeight(el.scrollHeight);
    };

    measure();

    const ro = new ResizeObserver(() => measure());
    ro.observe(el);

    const onResize = () => measure();
    window.addEventListener('resize', onResize);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [activeFilterTab, canCreateCustomReports]);

  // Drive animation on open/close transitions
  useEffect(() => {
    if (!canCreateCustomReports) return;
    const el = collapsibleInnerRef.current;
    if (!el) return;

    if (!customSectionCollapsed) {
      setMaxHeight(0);
      void el.getBoundingClientRect();
      requestAnimationFrame(() => setMaxHeight(el.scrollHeight));
    } else {
      setMaxHeight(el.scrollHeight + 5);
      requestAnimationFrame(() => setMaxHeight(0));
    }
  }, [canCreateCustomReports, customSectionCollapsed]);

  // Keep height in sync while open
  useEffect(() => {
    if (canCreateCustomReports && !customSectionCollapsed) {
      setMaxHeight(contentHeight);
    }
  }, [canCreateCustomReports, contentHeight, customSectionCollapsed]);

  const onContentChangeHandler = async (id: string) => {
    if (id === activeFilterTab) return;

    if (saveReportsFiltersPreferences) {
      await saveReportsFiltersPreferences(activeFilterTab);
    }
    setActiveFilterTab(id);
  };

  return (
    <Reporting.Container className="reporting-content" fetching={fetching}>
      {activeTab === tabs.predefined && <PredefinedReportsTab />}
      {activeTab === tabs.custom && (
        <>
          {useAutomatedReports && (
            <ReportsTopBar>
              <div className="copy">
                <span className="icon-pill" aria-hidden>
                  <span className="icon-inner">
                    <Icon icon="CalendarTransparent" size="default" stroke="#ffffff" />
                  </span>
                </span>
                <div className="text">
                  <p className="title">Set up your report schedule below.</p>
                  <p className="subtitle">Pick the reports you want, on the timing you prefer.</p>
                </div>
              </div>
              {!isMobile && (
                <Button $primary size="small" onClick={() => setActiveTab(tabs.scheduled)}>
                  {tabs.scheduled}
                </Button>
              )}
            </ReportsTopBar>
          )}
          <MainContent>
            <PopularTitle>Popular reports</PopularTitle>
            <PopularReportsProvider>
              <ReportingContentPopular />
            </PopularReportsProvider>
            {canCreateCustomReports ? (
              <CollapsedBannerContainer $collapsed={customSectionCollapsed}>
                <CollapsedBanner
                  $embedded={!customSectionCollapsed}
                  onClick={() => setCustomSectionCollapsed((v) => !v)}
                  aria-label={
                    customSectionCollapsed
                      ? 'Expand custom report section'
                      : 'Collapse custom report section'
                  }
                  aria-expanded={!customSectionCollapsed}
                  aria-controls="custom-section-collapsible"
                >
                  <span className="banner-text">
                    <strong>Prefer a custom report?</strong>
                    Select the exact metrics that matter to you.
                  </span>
                  <span className="chevron-pill" aria-hidden>
                    <Icon
                      icon={customSectionCollapsed ? 'ChevronDownPurple' : 'ChevronUpPurple'}
                      size="small"
                    />
                  </span>
                </CollapsedBanner>
                <CustomSectionCollapsable
                  id="custom-section-collapsible"
                  aria-hidden={customSectionCollapsed}
                  $open={!customSectionCollapsed}
                  $maxHeight={maxHeight}
                >
                  {!customSectionCollapsed && (
                    <ReportingContentFilter onFilterTabChange={onContentChangeHandler} />
                  )}
                  <div className="collapsible-clip">
                    <div className="collapsible-content" ref={collapsibleInnerRef}>
                      <ReportingFilters
                        filtersType={activeFilterTab}
                        hasSkillDive={accounts?.account_addons?.count > 0}
                        sonarEnabled={accounts?.sonar_enabled}
                      />
                    </div>
                  </div>
                </CustomSectionCollapsable>
              </CollapsedBannerContainer>
            ) : (
              <RestrictedCustomReportsBanner
                role="region"
                aria-label={CUSTOM_REPORTS_RESTRICTED_COPY.accessibleLabel}
                data-testid={REPORTING_TEST_IDS.customReportsRestrictedBanner}
              >
                <span className="lock-icon" aria-hidden="true">
                  <RestrictedReportsLock focusable="false" />
                </span>
                <span className="copy">
                  <strong>{CUSTOM_REPORTS_RESTRICTED_COPY.title}</strong>
                  <span>{CUSTOM_REPORTS_RESTRICTED_COPY.description}</span>
                  <a href={ENTERPRISE_PLAN_URL} target="_blank" rel="noopener noreferrer">
                    {CUSTOM_REPORTS_RESTRICTED_COPY.learnMore}
                  </a>
                </span>
              </RestrictedCustomReportsBanner>
            )}
            <SectionDivider />
            <ReportingHistoryTable />
            {useAutomatedReports && isMobile && (
              <MobileFloatingButtonWrapper>
                <Button $primary onClick={() => setActiveTab(tabs.scheduled)}>
                  {tabs.scheduled}
                </Button>
              </MobileFloatingButtonWrapper>
            )}
          </MainContent>
        </>
      )}
      {activeTab === tabs.scheduled && (
        <ScheduledReportsTable
          onClose={() => setActiveTab(tabs.custom)}
          readOnly={!canCreateCustomReports}
        />
      )}
    </Reporting.Container>
  );
};

export default ReportingContent;
